import * as React from 'react';
import {useFormikContext} from 'formik';
import Row from 'components/Row';
import Column from 'components/Column';
import FormContainer from '../../containers/FormContainer';
import {ContentType, WebinarObject} from 'api/webinar/types';
import Upload from '../../../../Upload';
import {
  FileObject,
  FileType,
  getImageByPreview,
} from 'api/library';
import styled from 'styled-components';
import Radio from 'components/Radio';
import {WebinarBanner} from 'components/WebinarBanner';
import WebinarBuilderContext from '../../../CourseBuilderContext';

const Container = styled.div`
  gap: 20px;
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

const Preview = styled.img`
  width: 100%;
  height: 480px;
  object-fit: cover;
  border-radius: 5px;
`;

const Video = styled.video`
  width: 100%;
  height: 480px;
  object-fit: cover;
  border-radius: 5px;
`;

/**
 * The before plug form part
 * @return {React.ReactElement}
 * @constructor
 */
export default function BeforePlugForm(): React.ReactElement {
  const formContext = useFormikContext<Partial<WebinarObject>>();
  const webinarContext = React.useContext(WebinarBuilderContext);

  /**
   * Handle date change
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    formContext.setFieldValue('plugs.beforePlug.type', event.target.value);
    formContext.setFieldValue('plugs.beforePlug.file', undefined);
    formContext.submitForm();
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    const plug = formContext.values.plugs?.beforePlug;
    switch (plug?.type) {
      case 'default': return (
        <Row>
          <Column>
            <WebinarBanner
              title={webinarContext.webinar?.title}
              date={formContext.values?.date}
              recordTime={formContext.values?.recordTime}
            />
          </Column>
        </Row>
      );
      case 'image': return (
        <Row>
          <Column>
            {plug?.file ? (
              <Preview src={getImageByPreview(plug?.file)} />
            ) : (
              <Upload
                type={FileType.Image}
                picker={{
                  onSelect(attached: FileObject[]) {
                    formContext.setFieldValue('plugs.beforePlug.file', attached[0]);
                    formContext.submitForm();
                  }
                }}
                onUpload={(file) => {
                  formContext.setFieldValue('plugs.beforePlug.file', file);
                  formContext.submitForm();
                }}
              />
            )}
          </Column>
        </Row>
      );
      case 'video': return (
        <Row>
          <Column>
            {plug?.file ? (
              <Video src={getImageByPreview(plug?.file)} />
            ) : (
              <Upload
                type={FileType.Video}
                picker={{
                  onSelect(attached: FileObject[]) {
                    formContext.setFieldValue('plugs.beforePlug.file', attached[0]);
                    formContext.submitForm();
                  }
                }}
                onUpload={(file) => {
                  formContext.setFieldValue('plugs.beforePlug.file', file);
                  formContext.submitForm();
                }}
              />
            )}
          </Column>
        </Row>
      );
      default: return <React.Fragment />
    }
  }

  return (
    <FormContainer title="До начала трансляции показывать ...">
      <Row>
        <Column>
          <Row>
            <Column>
              <Container>
                <Radio
                  name="beforePlug"
                  label="Заглушку"
                  value="default"
                  checked={webinarContext.webinar?.plugs?.beforePlug.type === 'default'}
                  onChange={handleChange}
                />
                <Radio
                  name="beforePlug"
                  label="Изображение"
                  value="image"
                  checked={webinarContext.webinar?.plugs?.beforePlug.type === 'image'}
                  onChange={handleChange}
                />
                <Radio
                  name="beforePlug"
                  label="Видео"
                  value="video"
                  checked={webinarContext.webinar?.plugs?.beforePlug.type === 'video'}
                  onChange={handleChange}
                />
              </Container>
            </Column>
          </Row>
          {renderContent()}
        </Column>
      </Row>
    </FormContainer>
  );
}
